import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarCheck, CheckCircle2, Compass, Send } from "lucide-react";
import { toast } from "sonner";
import { WeaveLogo, WeaveMark } from "@/components/WeaveLogo";
import { MaskedLine, FadeUp, Overline } from "@/components/Motion";
import { DepositButton } from "@/components/DepositButton";
import { ProspectusDialog } from "@/components/ProspectusDialog";
import { registerInterest } from "@/lib/api";

const MURAL_IMG = "/assets/change-mural.webp";
const TIDYCAL_URL = "https://tidycal.com/the-weave/30-minute-meeting";

const Header = () => (
  <header className="fixed top-0 inset-x-0 z-50 bg-weave-ink/85 backdrop-blur-xl border-b border-weave-cream/10">
    <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
      <Link to="/" aria-label="The Weave home" data-testid="change-header-home-link">
        <WeaveLogo dark />
      </Link>
      <nav className="flex items-center gap-3 sm:gap-6" aria-label="Change campaign navigation">
        <ProspectusDialog
          triggerText="Download prospectus"
          testId="change-header-prospectus-button"
          triggerClassName="hidden sm:inline-flex text-sm font-medium text-weave-cream/80 hover:text-weave-yellow transition-colors duration-300"
        />
        <a
          href="#change-register"
          data-testid="change-header-register-link"
          className="inline-flex items-center gap-2 bg-weave-yellow text-weave-ink hover:bg-[#e5ab38] transition-colors duration-300 rounded-full px-5 sm:px-6 py-2.5 text-sm font-medium"
        >
          Register your interest <ArrowRight size={15} />
        </a>
      </nav>
    </div>
  </header>
);

const Hero = () => (
  <section className="relative min-h-[94vh] bg-weave-ink text-weave-cream overflow-hidden pt-16" data-testid="change-hero-section">
    <div className="absolute inset-0 opacity-[0.06]" aria-hidden="true">
      <WeaveMark size={720} className="absolute -right-40 -top-32 rotate-12" />
    </div>
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center py-20 md:py-28">
      <div className="lg:col-span-6 relative z-10">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-xs font-bold uppercase tracking-[0.25em] text-weave-yellow mb-8"
          data-testid="change-hero-overline"
        >
          The Weave Founder Academy · April 2027
        </motion.p>
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.06]" data-testid="change-hero-headline" aria-label="Don’t leave this change to chance.">
          <MaskedLine delay={0.15}>Don’t leave this</MaskedLine>
          <MaskedLine delay={0.32}>
            <span className="text-weave-yellow font-normal">change to chance.</span>
          </MaskedLine>
        </h1>
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.62 }}
          className="mt-6 max-w-xl bg-weave-cream text-weave-ink rounded-2xl p-5 md:p-6 border border-weave-cream/20 shadow-xl"
          data-testid="change-academy-pricing"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-weave-red">
            Not sure whether founding is your next chapter?
          </p>
          <p className="mt-3 text-lg font-medium">You don’t need to decide that today.</p>
          <p className="mt-1.5 text-sm text-weave-slate leading-relaxed">
            Personal Clarity gives you ten structured weeks — including the Founder Capacity Index —
            to explore what you want, understand what you bring, and work out whether building
            something of your own genuinely fits your life. £499 including VAT. If you decide you
            want to build something, £2,501 secures the remaining Business and External Clarity
            sections — £3,000 in total.
          </p>
          <div className="mt-4">
            <DepositButton plan="clarity" testId="change-clarity-button" />
          </div>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.82 }}
          className="mt-7 max-w-xl"
          data-testid="change-hero-subcopy"
        >
          <p className="text-lg md:text-xl text-weave-cream/82 leading-relaxed">
            If you are thinking about building something of your own, you do not need another vague
            promise. The Weave Founder Academy gives you a structured year of mindset work,
            real-world experiments, mentoring, peer learning, and practical business education — so
            change becomes something you practise, not something you wait for.
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.86 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <ProspectusDialog
            triggerText="Download the Personal Clarity prospectus"
            testId="change-hero-prospectus-button"
            triggerClassName="group inline-flex items-center gap-3 bg-weave-yellow text-weave-ink hover:bg-[#e5ab38] transition-colors duration-300 rounded-full px-8 py-4 font-medium"
          />
          <a
            href={TIDYCAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="change-hero-consult-link"
            className="group inline-flex items-center gap-2 text-weave-yellow font-medium"
          >
            <CalendarCheck size={18} />
            Book a 30-minute Founder Clarity Consult
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, x: 36 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.9, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="lg:col-span-6 relative"
      >
        <div className="absolute -inset-3 md:-inset-5 border border-weave-yellow/25 rounded-[2rem] rotate-1" aria-hidden="true" />
        <div className="relative overflow-hidden rounded-[1.75rem] shadow-2xl bg-weave-teal">
          <img
            src={MURAL_IMG}
            alt="Wall mural showing the words: Life does not get better by chance, it gets better by change"
            className="w-full aspect-[4/3] object-cover"
            data-testid="change-hero-image"
          />
          <div className="px-5 py-4 md:px-6 md:py-5 text-weave-cream">
            <p className="text-sm font-medium leading-snug" data-testid="change-hero-image-caption">
              Change is built through practice — one deliberate step at a time.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

const AcademyCta = () => {
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Please add your email so we can keep you posted.");
      return;
    }
    setBusy(true);
    try {
      await registerInterest({
        type: "register_interest",
        ...form,
        message: "Change campaign landing page",
      });
      setDone(true);
      toast.success("You're on the list for the April 2027 cohort.");
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="change-register" className="py-24 md:py-32 bg-weave-sand/60 border-y border-weave-line" data-testid="change-academy-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-7">
          <FadeUp>
            <Overline>From intention to evidence</Overline>
            <h2 className="mt-6 text-3xl md:text-5xl font-light tracking-tight text-weave-ink leading-[1.15] max-w-2xl" data-testid="change-academy-headline">
              The Academy helps you turn a possible future into a practised one.
            </h2>
            <p className="mt-8 text-lg md:text-xl text-weave-slate leading-relaxed max-w-2xl">
              Over twelve months, you will move from a cloudy sense of what you want to change, to a
              tested side hustle and the makings of a business that is genuinely yours — at a pace a
              full life can sustain.
            </p>
            <ul className="mt-10 space-y-5 max-w-xl" data-testid="change-academy-benefits">
              {[
                "Sophrology-informed mindset and leadership practice.",
                "Real-world experiments, customer evidence, and practical business education.",
                "Mentoring, peer learning, and a rhythm that fits around work and family.",
              ].map((item) => (
                <li key={item} className="flex gap-4 items-start">
                  <Compass size={19} className="mt-1 shrink-0 text-weave-teal" />
                  <span className="text-base md:text-lg text-weave-slate leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-10 text-xl font-light text-weave-ink tracking-wide">
              Find clarity. Test possibility. <span className="text-weave-teal">Build what matters.</span>
            </p>
          </FadeUp>
        </div>

        <div className="lg:col-span-5">
          <FadeUp delay={0.15} className="bg-weave-cream text-weave-ink rounded-2xl p-8 md:p-10 border border-weave-line shadow-sm" data-testid="change-register-card">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-weave-red bg-weave-red/10 rounded-full px-4 py-2">
              First cohort · April 2027
            </span>
            <h3 className="mt-6 text-xl md:text-2xl font-medium">Register your interest</h3>
            <p className="mt-3 text-sm text-weave-slate leading-relaxed">
              Leave your details and we will keep you posted as the first cohort takes shape. No
              obligation, no hard sell.
            </p>
            {done ? (
              <div className="mt-8 flex items-center gap-3 text-weave-teal" data-testid="change-interest-success">
                <CheckCircle2 size={22} />
                <p className="font-medium">Thank you — you&rsquo;re on the list.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="change-interest-form">
                <input
                  type="text"
                  aria-label="First name (optional)"
                  placeholder="First name (optional)"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  data-testid="change-interest-name-input"
                  className="w-full rounded-xl border border-weave-line bg-white px-4 py-3 text-sm text-weave-ink placeholder:text-weave-mute focus:border-weave-teal focus:outline-none transition-colors duration-200"
                />
                <input
                  type="email"
                  aria-label="Email address"
                  placeholder="Email address"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="change-interest-email-input"
                  className="w-full rounded-xl border border-weave-line bg-white px-4 py-3 text-sm text-weave-ink placeholder:text-weave-mute focus:border-weave-teal focus:outline-none transition-colors duration-200"
                />
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="change-interest-submit-button"
                  className="w-full inline-flex items-center justify-center gap-2 bg-weave-red text-white hover:bg-[#cf352e] transition-colors duration-300 rounded-full px-8 py-4 font-medium disabled:opacity-60"
                >
                  {busy ? "Registering…" : "Keep me posted"} <Send size={16} />
                </button>
              </form>
            )}
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="bg-weave-ink text-weave-cream border-t border-weave-cream/10" data-testid="change-footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 flex flex-col md:flex-row md:items-end justify-between gap-10">
      <div>
        <WeaveLogo dark />
        <p className="mt-4 text-sm text-weave-cream/70 max-w-sm leading-relaxed">
          Founder First; Business Second. Always.
        </p>
        <a
          href="https://www.wearetheweave.co.uk"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="change-footer-website-button"
          className="group mt-6 inline-flex items-center gap-2 border border-weave-cream/30 text-weave-cream hover:bg-weave-cream hover:text-weave-ink transition-colors duration-300 rounded-full px-6 py-3 text-sm font-medium"
        >
          Find out more about The Weave
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      </div>
      <p className="text-xs text-weave-cream/50 max-w-md leading-relaxed">
        The Weave Founder Academy is a developmental practice ground. It does not provide clinical,
        financial, or psychometric advice, and it makes no automated decisions about you.
      </p>
    </div>
    <div className="border-t border-weave-cream/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
        <p className="text-xs text-weave-cream/40 leading-relaxed" data-testid="change-footer-legal">
          Relocon t/a The Weave Limited · Company Registration Number 12213782 · VAT Registration
          Number 335471503 ·{" "}
          <a href="https://www.wearetheweave.co.uk" target="_blank" rel="noopener noreferrer" className="underline hover:text-weave-cream/70 transition-colors duration-200">
            www.wearetheweave.co.uk
          </a>{" "}
          ·{" "}
          <a href="https://www.wearetheweave.co.uk/privacy-policy" target="_blank" rel="noopener noreferrer" data-testid="change-footer-privacy-link" className="underline hover:text-weave-cream/70 transition-colors duration-200">
            Privacy Notice
          </a>
        </p>
      </div>
    </div>
  </footer>
);

export default function ChangeLanding() {
  return (
    <div data-testid="change-page">
      <Header />
      <Hero />
      <AcademyCta />
      <Footer />
    </div>
  );
}
